import { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useEventListener, usePrevious } from '@react-hookz/web';
import { useAgentState } from '@/context/agent-state.context';
import { getStoredRect } from '@/utils/capture-screen';

const AGENT_COLOR = '#8b5cf6';

interface OverlayHighlight {
  id: string;
  type: 'highlight';
  rect: DOMRect;
  label?: string;
}

interface OverlayArrow {
  id: string;
  type: 'arrow';
  from: { x: number; y: number };
  to: { x: number; y: number };
  label?: string;
}

type OverlayItem = OverlayHighlight | OverlayArrow;

let overlayIdCounter = 0;

const clearOverlayRef = { current: null as (() => void) | null };

export function clearOverlay(): void {
  clearOverlayRef.current?.();
}

function rectCenter(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

export function AgentOverlay({
  onCountChange,
}: {
  onCountChange?: (count: number) => void;
}) {
  const markerId = useId();
  const { registerToolCallListener, isLoading } = useAgentState();
  const [items, setItems] = useState<OverlayItem[]>([]);
  const prevIsLoading = usePrevious(isLoading);
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const updateItems = useCallback(
    (next: OverlayItem[] | ((prev: OverlayItem[]) => OverlayItem[])) => {
      setItems((prev) => {
        const result = typeof next === 'function' ? next(prev) : next;
        if (result.length !== prev.length) {
          onCountChangeRef.current?.(result.length);
        }
        return result;
      });
    },
    [],
  );

  const clearAll = useCallback(() => updateItems([]), [updateItems]);

  clearOverlayRef.current = clearAll;

  useEffect(() => {
    onCountChangeRef.current?.(0);
  }, []);

  // Clear annotations when a new turn starts
  useEffect(() => {
    if (isLoading && !prevIsLoading) {
      clearAll();
    }
  }, [isLoading, prevIsLoading, clearAll]);

  // Clear annotations on page interaction (scroll, click, navigation)
  const activeTarget = items.length > 0 ? window : null;
  const activeDoc = items.length > 0 ? document : null;

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-capture-exclude]')) return;
      clearAll();
    },
    [clearAll],
  );

  useEventListener(activeTarget, 'scroll', clearAll, {
    passive: true,
    capture: true,
  });
  useEventListener(activeTarget, 'popstate', clearAll);
  useEventListener(activeDoc, 'click', handleClick, { capture: true });

  useEffect(() => {
    return registerToolCallListener(
      (
        _toolCallId: string,
        toolName: string,
        args: Record<string, unknown>,
      ) => {
        if (toolName === 'clear_annotations') {
          updateItems([]);
          return;
        }

        if (toolName !== 'annotate_screen') return;

        const annotations = args.annotations as Array<Record<string, unknown>>;
        if (!Array.isArray(annotations)) return;

        const newItems: OverlayItem[] = [];

        for (const ann of annotations) {
          const id = `overlay-${++overlayIdCounter}`;

          if (ann.type === 'highlight') {
            const rect = getStoredRect(ann.elementId as number);
            if (!rect) continue;
            newItems.push({
              id,
              type: 'highlight',
              rect,
              label: ann.label as string | undefined,
            });
          } else if (ann.type === 'arrow') {
            const fromRect = getStoredRect(ann.fromElementId as number);
            const toRect = getStoredRect(ann.toElementId as number);
            if (!fromRect || !toRect) continue;
            newItems.push({
              id,
              type: 'arrow',
              from: rectCenter(fromRect),
              to: rectCenter(toRect),
              label: ann.label as string | undefined,
            });
          }
        }

        if (newItems.length > 0) {
          updateItems((prev) => [...prev, ...newItems]);
        }
      },
    );
  }, [registerToolCallListener, updateItems]);

  if (items.length === 0) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 41 }}
      data-capture-exclude
    >
      {items.map((item) => {
        if (item.type === 'highlight') {
          const pad = 5;
          return (
            <div
              key={item.id}
              style={{
                position: 'absolute',
                left: item.rect.left - pad,
                top: item.rect.top - pad,
                width: item.rect.width + pad * 2,
                height: item.rect.height + pad * 2,
                border: `2px solid ${AGENT_COLOR}`,
                borderRadius: 6,
                background: 'rgba(139, 92, 246, 0.08)',
                boxShadow: '0 0 12px rgba(139, 92, 246, 0.45)',
              }}
            >
              {item.label && (
                <Pill
                  text={item.label}
                  centerX={item.rect.width / 2 + pad}
                  topY={item.rect.height + pad * 2 + 6}
                />
              )}
            </div>
          );
        }

        return null;
      })}

      <svg
        className="absolute inset-0 w-full h-full overflow-visible"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <marker
            id={markerId}
            markerWidth="10"
            markerHeight="7"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={AGENT_COLOR} />
          </marker>
        </defs>
        {items
          .filter((item): item is OverlayArrow => item.type === 'arrow')
          .map((item) => {
            const midX = (item.from.x + item.to.x) / 2;
            const midY = (item.from.y + item.to.y) / 2;
            return (
              <g key={item.id}>
                <line
                  x1={item.from.x}
                  y1={item.from.y}
                  x2={item.to.x}
                  y2={item.to.y}
                  stroke={AGENT_COLOR}
                  strokeWidth={3}
                  markerEnd={`url(#${markerId})`}
                />
                {item.label && (
                  <foreignObject
                    x={midX - 100}
                    y={midY - 24}
                    width={200}
                    height={30}
                    overflow="visible"
                  >
                    <div className="flex justify-center">
                      <span
                        className="text-xs text-white px-2.5 py-0.5 rounded-full whitespace-nowrap"
                        style={{
                          background: AGENT_COLOR,
                          boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
                        }}
                      >
                        {item.label.slice(0, 80)}
                      </span>
                    </div>
                  </foreignObject>
                )}
              </g>
            );
          })}
      </svg>
    </div>
  );
}

function Pill({
  text,
  centerX,
  topY,
}: {
  text: string;
  centerX: number;
  topY: number;
}) {
  return (
    <div
      className="absolute -translate-x-1/2 text-xs text-white px-2.5 py-0.5 rounded-full whitespace-nowrap max-w-80 overflow-hidden text-ellipsis pointer-events-none"
      style={{
        left: centerX,
        top: topY,
        background: AGENT_COLOR,
        boxShadow: '0 2px 8px rgba(139, 92, 246, 0.35)',
      }}
    >
      {text.slice(0, 80)}
    </div>
  );
}
